import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, X, Phone, Mail } from "lucide-react";
import { CONTACT_PHONE, CONTACT_PHONE_HREF, CONTACT_EMAIL, CONTACT_EMAIL_HREF } from "@/components/Navbar";

const AUTO_OPEN_DELAY_MS = 4000;
const DISMISS_KEY = "help-widget-dismissed";

// Routes where the floating widget would overlap primary UI (message composer,
// booking form). Hidden on these to avoid intercepting pointer events.
const HIDE_ON_ROUTES = ["/messages"];

export default function HelpWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [autoShown, setAutoShown] = useState(false);

  // Auto-open once per session after a short delay
  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    if (HIDE_ON_ROUTES.some((r) => pathname.startsWith(r))) return;
    const t = setTimeout(() => {
      setOpen(true);
      setAutoShown(true);
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  const dismiss = () => {
    setOpen(false);
    if (autoShown) sessionStorage.setItem(DISMISS_KEY, "1");
  };

  if (HIDE_ON_ROUTES.some((r) => pathname.startsWith(r))) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" data-testid="help-widget-root">
      {open && (
        <div
          role="dialog"
          aria-live="polite"
          data-testid="help-widget-card"
          className="w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white shadow-2xl border border-stone2-200 overflow-hidden animate-fade-in-up"
        >
          {/* Card header — lighthouse ochre gradient */}
          <div className="relative bg-gradient-to-br from-ochre-500 to-ochre-600 p-5 pb-4 text-white">
            <button
              type="button"
              onClick={dismiss}
              data-testid="help-widget-close"
              aria-label="Close help widget"
              className="absolute top-3 right-3 p-1.5 rounded-full text-white/80 hover:bg-white/20 transition-colors duration-200"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/25 backdrop-blur mb-2">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-semibold text-lg leading-snug pr-8">
              Need help finding someone in your area?
            </h3>
            <p className="mt-2 text-sm text-white/90 leading-relaxed">
              We can help you. Contact us today — a real person will match you with the right sitter.
            </p>
          </div>

          {/* Actions */}
          <div className="p-4 space-y-2">
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="help-widget-phone"
              className="flex items-center gap-3 rounded-xl border border-stone2-200 bg-white hover:border-sage-400 hover:bg-sage-50 p-3 transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-sage-100 text-sage-700">
                <Phone className="w-4 h-4" />
              </span>
              <span className="flex-1">
                <span className="block text-xs uppercase tracking-[0.18em] text-stone2-500 font-semibold">Call us</span>
                <span className="block text-sm font-heading font-bold text-sage-900">{CONTACT_PHONE}</span>
              </span>
            </a>
            <a
              href={CONTACT_EMAIL_HREF}
              data-testid="help-widget-email"
              className="flex items-center gap-3 rounded-xl border border-stone2-200 bg-white hover:border-sage-400 hover:bg-sage-50 p-3 transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-sage-100 text-sage-700">
                <Mail className="w-4 h-4" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs uppercase tracking-[0.18em] text-stone2-500 font-semibold">Email us</span>
                <span className="block text-sm font-heading font-bold text-sage-900 truncate">{CONTACT_EMAIL}</span>
              </span>
            </a>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close help" : "Open help"}
        data-testid="help-widget-toggle"
        className={`group relative inline-flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ochre-500 focus:ring-offset-2 ${
          open
            ? "bg-white text-sage-900 border border-stone2-200"
            : "bg-ochre-600 hover:bg-ochre-700 text-white"
        }`}
      >
        <span className="relative flex items-center justify-center w-6 h-6">
          {!open && (
            <span className="absolute inset-0 rounded-full bg-ochre-400 opacity-60 animate-ping" aria-hidden="true"></span>
          )}
          <LifeBuoy className="w-5 h-5 relative" />
        </span>
        <span className="text-sm font-semibold">
          {open ? "Close" : "Need help?"}
        </span>
      </button>
    </div>
  );
}
